import { useEffect, useState } from "react";
import { CalendarCheck, Loader2, Building2, Mail, User, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import USPhoneInput from "./USPhoneInput";

const PORTFOLIO_SIZES = [
  { value: "1-5",    label: "1-5 properties" },
  { value: "6-25",   label: "6-25 properties" },
  { value: "26-100", label: "26-100 properties" },
  { value: "100+",   label: "100+ properties" },
];

/**
 * Landing-page "Book a demo" form.
 *
 * Fetches bookable slots from the backend (10 business days × 3 slots each),
 * captures a lightweight qualified-lead payload, and on submit shows the
 * confirmation state promising a calendar invite by email. Backend sends an
 * ICS attachment so the meeting auto-adds to Google Calendar / Outlook /
 * Apple Calendar — no OAuth handshake required from the prospect.
 *
 * Rate-limited server-side (5 / IP / 10 minutes). All fields except `message`
 * and `phone` are required.
 */
export default function DemoRequestForm() {
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    portfolio_size: "6-25", preferred_slot: "", message: "",
  });

  useEffect(() => {
    api.get("/public/demo-requests/available-slots")
      .then((r) => {
        const s = r.data?.slots || [];
        setSlots(s);
        setForm((prev) => ({ ...prev, preferred_slot: prev.preferred_slot || (s[0]?.iso ?? "") }));
      })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, []);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errors = [];
    if (!form.name.trim()) errors.push("name");
    if (!form.email.trim() || !form.email.includes("@")) errors.push("email");
    if (!form.preferred_slot) errors.push("slot");
    if (errors.length) {
      toast.error(`Please fill in: ${errors.join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      // Preserve attribution if the landing page was visited with ?utm_source=
      const utmSource = new URLSearchParams(window.location.search).get("utm_source") || undefined;
      const { data } = await api.post("/public/demo-requests", {
        ...form,
        utm_source: utmSource,
      });
      const when = new Date(data.preferred_slot);
      setSubmitted({
        slotLabel: when.toLocaleString(undefined, {
          weekday: "long", month: "long", day: "numeric",
          hour: "2-digit", minute: "2-digit", timeZoneName: "short",
        }),
        email: form.email,
        id: data.id,
      });
      toast.success("Demo booked — check your email for the calendar invite.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't book the demo");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
        data-testid="demo-form-success"
      >
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <div className="overline text-emerald-700">Confirmed</div>
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>
          Your RealtyVault demo is booked.
        </h3>
        <div className="mt-5 inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-sm font-mono text-sm">
          <CalendarCheck className="w-4 h-4" />
          {submitted.slotLabel}
        </div>
        <p className="text-sm text-slate-600 mt-6 leading-relaxed">
          A calendar invite (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded-sm">.ics</code>) is on its way to{" "}
          <b>{submitted.email}</b> — just click it and your calendar (Google, Outlook, Apple) will add the meeting automatically.
          <br /><br />
          Got questions? Reply to the confirmation email anytime.
        </p>
        <div className="mt-6 text-xs font-mono text-slate-400 uppercase tracking-widest">
          Reference: {submitted.id?.slice(0, 8)}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
      data-testid="demo-request-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <CalendarCheck className="w-4 h-4 text-amber-600" />
        <div className="overline text-amber-700">Book a demo</div>
      </div>
      <h3 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
        See RealtyVault in action — on your portfolio.
      </h3>
      <p className="text-sm text-slate-600 mt-2">
        30-minute walkthrough tailored to your CRE portfolio size. No sales pitch — just the product.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="Full name" required icon={<User className="w-3.5 h-3.5" />}>
          <input
            type="text" required
            value={form.name} onChange={change("name")}
            className="input-clean w-full"
            placeholder="Alex Morgan"
            data-testid="demo-form-name"
          />
        </Field>
        <Field label="Work email" required icon={<Mail className="w-3.5 h-3.5" />}>
          <input
            type="email" required
            value={form.email} onChange={change("email")}
            className="input-clean w-full"
            placeholder="alex@acmecre.com"
            data-testid="demo-form-email"
          />
        </Field>
        <Field label="Company" icon={<Building2 className="w-3.5 h-3.5" />}>
          <input
            type="text"
            value={form.company} onChange={change("company")}
            className="input-clean w-full"
            placeholder="Acme CRE Holdings"
            data-testid="demo-form-company"
          />
        </Field>
        <Field label="Phone (optional)" icon={<Phone className="w-3.5 h-3.5" />}>
          <input
            type="tel"
            value={form.phone} onChange={change("phone")}
            className="input-clean w-full"
            placeholder="+1 555 0123"
            data-testid="demo-form-phone"
          />
        </Field>
        <Field label="Portfolio size">
          <select
            value={form.portfolio_size} onChange={change("portfolio_size")}
            className="input-clean w-full"
            data-testid="demo-form-portfolio-size"
          >
            {PORTFOLIO_SIZES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Preferred time" required>
          <select
            value={form.preferred_slot} onChange={change("preferred_slot")}
            className="input-clean w-full"
            required
            disabled={loadingSlots}
            data-testid="demo-form-slot"
          >
            {loadingSlots && <option>Loading…</option>}
            {!loadingSlots && slots.length === 0 && <option value="">No slots available</option>}
            {!loadingSlots && slots.map((s) => (
              <option key={s.iso} value={s.iso}>{s.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="What would you like to see?" className="mt-4">
        <textarea
          value={form.message} onChange={change("message")}
          className="input-clean w-full resize-none"
          rows={3}
          placeholder="e.g. tenant rent collection, cap-rate tracking, investor reports…"
          data-testid="demo-form-message"
        />
      </Field>

      <button
        type="submit"
        disabled={submitting || loadingSlots || !form.preferred_slot}
        className="btn-primary-dark w-full mt-5 py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="demo-form-submit"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</>
        ) : (
          <>Book my 30-minute demo <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
      <div className="text-[11px] font-mono text-slate-400 text-center mt-3">
        We'll send a calendar invite to your inbox. No sales spam, ever.
      </div>
    </form>
  );
}

function Field({ label, required, icon, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-slate-600 flex items-center gap-1 mb-1">
        {icon}{label}{required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
